import React, { useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../../../components/AppIcon";

const CategoryCards = ({ currentTheme, onThemeChange }) => {
  const [hoveredCategory, setHoveredCategory] = useState(null);

  const categories = [
    {
      id: "events",
      title: "Events & Weddings",
      description:
        "Romantic, timeless visual storytelling that captures life's precious moments with cinematic elegance.",
      icon: "Heart",
      image:
        "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=600&h=400&fit=crop",
      route: "/services-architecture-hub",
      stats: "200+ Celebrations",
      color: "from-rose-400/20 to-amber-400/20",
      accentColor: "text-rose-500",
      bgColor: "bg-rose-50",
      theme: "events",
      features: ["4K Cinematic", "Same Day Edit", "Drone Footage"],
    },
    {
      id: "realestate",
      title: "Real Estate",
      description:
        "Clean, architectural presentations that showcase properties with sophisticated visual appeal and clear ROI.",
      icon: "Building",
      image:
        "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop",
      route: "/services-architecture-hub",
      stats: "150+ Properties",
      color: "from-blue-400/20 to-cyan-400/20",
      accentColor: "text-blue-600",
      bgColor: "bg-blue-50",
      theme: "realestate",
      features: ["Virtual Tours", "Aerial Views", "Floor Plans"],
    },
    {
      id: "marketing",
      title: "Brand & Marketing",
      description:
        "Bold, contemporary content that cuts through noise and drives engagement metrics with innovative storytelling.",
      icon: "Zap",
      image:
        "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=600&h=400&fit=crop",
      route: "/services-architecture-hub",
      stats: "100+ Campaigns",
      color: "from-orange-400/20 to-red-400/20",
      accentColor: "text-orange-600",
      bgColor: "bg-orange-50",
      theme: "marketing",
      features: ["Social Media", "Brand Films", "Product Launch"],
    },
    {
      id: "design",
      title: "Design Fiction",
      description:
        "Imaginative, conceptual work that visualizes tomorrow's possibilities through experimental visual narratives.",
      icon: "Sparkles",
      image:
        "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=600&h=400&fit=crop",
      route: "/services-architecture-hub",
      stats: "50+ Concepts",
      color: "from-purple-400/20 to-pink-400/20",
      accentColor: "text-purple-600",
      bgColor: "bg-purple-50",
      theme: "design",
      features: ["3D Animation", "VFX", "Motion Graphics"],
    },
  ];

  const handleCategoryHover = (theme, categoryId) => {
    setHoveredCategory(categoryId);
    onThemeChange(theme);
  };

  const handleCategoryLeave = () => {
    setHoveredCategory(null);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-background via-surface/30 to-background relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #000000 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        ></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Enhanced Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-20">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-medium mb-6">
            <Icon name="Sparkles" size={16} />
            <span>Our Expertise</span>
          </div>

          <h2 className="text-fluid-4xl font-display font-bold text-foreground mb-8 leading-tight">
            Creative Excellence
            <span className="block text-gradient bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
              Across Industries
            </span>
          </h2>

          <p className="text-fluid-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
            Discover how Skycrest Studios adapts our visual storytelling mastery
            across diverse industries, creating authentic experiences that
            elevate your brand and drive meaningful results.
          </p>
        </div>

        {/* Enhanced Category Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
          {categories?.map((category) => (
            <div
              key={category?.id}
              className="group relative"
              onMouseEnter={() =>
                handleCategoryHover(category?.theme, category?.id)
              }
              onMouseLeave={handleCategoryLeave}
            >
              <Link to={category?.route} className="block h-full">
                <div className="relative overflow-hidden rounded-3xl bg-card border border-border/50 hover-lift transition-all duration-500 h-full shadow-md hover:shadow-2xl">
                  {/* Enhanced Image Container */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={category?.image}
                      alt={category?.title}
                      className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                    />

                    {/* Multi-layer Overlay */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-t ${category?.color} opacity-60 group-hover:opacity-40 transition-opacity duration-300`}
                    ></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                    {/* Enhanced Icon Badge */}
                    <div
                      className={`absolute top-4 right-4 w-14 h-14 ${category?.bgColor} rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 shadow-lg`}
                    >
                      <Icon
                        name={category?.icon}
                        size={24}
                        className={`${category?.accentColor} group-hover:scale-110 transition-transform duration-300`}
                      />
                    </div>

                    {/* Hover Overlay with Features */}
                    <div
                      className={`absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end p-6 ${
                        hoveredCategory === category?.id ? "opacity-100" : ""
                      }`}
                    >
                      <div className="space-y-3">
                        <div className="text-white text-sm font-medium">
                          Key Features:
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {category?.features?.map((feature, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-white/20 rounded-full text-xs text-white/90"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Content */}
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold text-foreground group-hover:text-accent transition-colors duration-300">
                        {category?.title}
                      </h3>
                      <div
                        className={`text-sm font-medium ${category?.accentColor} opacity-80 bg-muted/50 px-3 py-1 rounded-full`}
                      >
                        {category?.stats}
                      </div>
                    </div>

                    <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                      {category?.description}
                    </p>

                    {/* Enhanced CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-border/50">
                      <div className="flex items-center text-accent font-medium text-sm group-hover:text-accent/80 transition-colors duration-300">
                        <span>Explore Services</span>
                        <Icon
                          name="ArrowRight"
                          size={16}
                          className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
                        />
                      </div>

                      <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                        <Icon
                          name="Star"
                          size={12}
                          className="text-yellow-500"
                        />
                        <span>5.0</span>
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Hover Effect Border */}
                  <div
                    className={`absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-accent/30 transition-all duration-300 pointer-events-none ${
                      hoveredCategory === category?.id ? "border-accent/50" : ""
                    }`}
                  ></div>

                  {/* Glow Effect */}
                  <div
                    className={`absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${
                      hoveredCategory === category?.id ? "opacity-100" : ""
                    }`}
                    style={{
                      boxShadow: `0 0 40px rgba(var(--accent-rgb, 232, 180, 160), 0.2)`,
                    }}
                  ></div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Enhanced Bottom CTA */}
        <div className="text-center mt-20">
          <div className="inline-flex flex-col sm:flex-row items-center gap-6">
            <Link
              to="/dynamic-portfolio-ecosystem"
              className="group inline-flex items-center space-x-3 px-8 py-4 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span>View Complete Portfolio</span>
              <Icon
                name="ExternalLink"
                size={18}
                className="group-hover:scale-110 transition-transform duration-300"
              />
            </Link>

            <div className="text-sm text-muted-foreground">
              Or call us at{" "}
              <a
                href="tel:+15089805187"
                className="font-medium text-accent hover:text-accent/80 transition-colors"
              >
                (508) 980-5187
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoryCards;
